import React, { ReactNode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import "./ConfirmationDialog.scss";
import { getDialogButtons, type DialogButton } from "./DialogUtils";
import { AlertIndicator, SeemaError } from "../../components";
import { IconButton } from "../../controls";

export const DIALOG_CONTAINER_CLASS = "popup-dialog-container";
export const DIALOG_ROOT_ID = "popup-dialog-container";

export type ConfirmationDialogType = "error" | "warning" | "";

export interface ConfirmationDialogProps {
  message?: ReactNode;
  buttons: DialogButton[];
  type?: ConfirmationDialogType;
  showCloseIcon?: boolean;
  title?: ReactNode;
  icon?: string;
  alignLeft?: boolean;
  overlay?: boolean;
  description?: ReactNode;
  className?: string;
  noPadding?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  message,
  buttons,
  type = "",
  showCloseIcon = true,
  title,
  icon,
  alignLeft = false,
  overlay = true,
  description,
  className = "",
  noPadding = false,
}) => {
  const getAnimation = useMemo(() => {
    switch (type) {
      case "error":
        return <SeemaError />;

      case "warning":
        return <AlertIndicator type="warning" content={description} />;

      default:
        return null;
    }
  }, [type]);
  return (
    <>
      {overlay === false ? null : (
        <div className={`popup-dialog-overlay modal-dialog-overlay`} />
      )}
      <div
        className={`popup-dialog-container confirmation-dialog modal-dialog-container ${className} ${type} ${
          alignLeft ? "is-align-left" : ""
        }`}
        data-testid={"confirmation-dialog"}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={`modal-dialog-content`}>
          <div className={`modal-header`}>
            {title && (
              <div className={`modal-header-title-wrap`}>
                {icon ? <i className={icon} /> : null}
                <h3 className="modal-title">{title}</h3>
              </div>
            )}
            {showCloseIcon && (
              <IconButton
                data-testid={"confirmation-dialog"}
                isIcon="seem-icon-close"
                type={"button"}
                size={"large"}
                buttonStyle={`secondary`}
                onClick={(e) => {
                  removePopupDialog();
                  e.stopPropagation();
                }}
              />
            )}
          </div>
          <div className="modal-content">
            {getAnimation && (
              <div
                className={`confirmation-dialog-character-container ${type}`}
              >
                {getAnimation}
              </div>
            )}

            <div
              className={`popup-dialog-text ${!noPadding ? "has-padding" : ""}`}
            >
              {message}
            </div>
          </div>

          <div
            className={`form-button-group modal-buttons-container popup-dialog-buttons ${
              buttons.length >= 3 ? "has-3-btns" : ""
            }`}
          >
            {getDialogButtons(buttons, removePopupDialog)}
          </div>
        </div>
      </div>
    </>
  );
};

const createPopupDialog = (props: ConfirmationDialogProps) => {
  let root;
  let divTarget = document.getElementById(DIALOG_ROOT_ID);
  if (divTarget) {
    root = createRoot(divTarget);
    root.render(<ConfirmationDialog {...props} />);
  } else {
    divTarget = document.createElement("div");
    divTarget.id = DIALOG_ROOT_ID;
    divTarget.className = DIALOG_CONTAINER_CLASS;
    root = createRoot(divTarget);
    root.render(<ConfirmationDialog {...props} />);
    document.body.appendChild(divTarget);
  }
};

const removePopupDialog = () => {
  const target = document.getElementById(DIALOG_ROOT_ID);
  if (target) {
    const root = createRoot(target);
    root.unmount();
    target.parentNode?.removeChild(target);
  }
};

export const showDialog = (props: ConfirmationDialogProps) => {
  createPopupDialog(props);
};

export const showErrorDialog = (
  props: Omit<ConfirmationDialogProps, "buttons"> & {
    buttons?: DialogButton[];
  },
) => {
  createPopupDialog({
    ...props,
    title: "Error",
    type: "error",
    buttons: props.buttons
      ? [...props.buttons, { label: "Close" }]
      : [{ label: "Close", shouldStopPropagation: true }],
  });
};
